import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import {
  fetchModules,
  addModule,
  editModule,
  deleteModuleById,
  fetchModulesByProject,
} from "../redux/actions/moduleAction";
import { Module } from "../types/Module";
import ModuleForm from "../components/ModuleForm";
import ModuleList from "../components/ModuleList";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import PageContainer from "../components/custom/PageContainer";
import Main from "../components/custom/Main";
import { useToast } from "../components/toast/ToastProvider";
import ModuleFilter from "../components/ModuleFilter";

const ModulePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const modules = useSelector((state: StoreState) => state.module);
  const [selected, setSelected] = useState<Module | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async (mod: Module) => {
    setLoading(true);
    try {
      if (selected) {
        await dispatch(editModule(mod.id, mod));
        toast("Module updated successfully");
      } else {
        await dispatch(addModule(mod));
        toast("Module added successfully");
      }
      dispatch(fetchModules());
      setSelected(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving module");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this module?")) {
      setLoading(true);
      try {
        await dispatch(deleteModuleById(id));
        toast("Module deleted successfully");
      } catch (error) {
        toast("Error deleting module");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (mod: Module) => {
    setSelected(mod);
    setShowModal(true);
  };

  useEffect(() => {
    setLoading(true);
    dispatch(fetchModules()).finally(() => setLoading(false));
  }, [dispatch]);

  const [filters, setFilters] = useState({
    project: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    setAppliedFilters(filters);
    setIsFiltered(true);
    localStorage.setItem("moduleFilter", JSON.stringify(filters));
    if (filters.project) {
      dispatch(fetchModulesByProject(parseInt(filters.project)));
    } else {
      dispatch(fetchModules());
    }
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      project: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setIsFiltered(false);
    localStorage.removeItem("moduleFilter");
  };
  useEffect(() => {
    const savedFilters = localStorage.getItem("moduleFilter");
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      setFilters(parsed);
      setAppliedFilters(parsed);
      setIsFiltered(true);
    }
  }, []);

  return (
    <Main>
      <PageContainer
        title="Module Management"
        buttonText={loading ? "Loading..." : "ADD"}
        onButtonClick={() => setShowModal(true)}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          headerText={selected ? "Update Module" : "Add Module"}
        >
          <ModuleForm
            initialData={selected}
            onSave={handleSave}
            clearAfterSubmit={() => setSelected(null)}
          />
        </Modal>
        <CollapseCard title="Module Filter">
          <ModuleFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFind={handleFindFilters}
            onReset={handleResetFilters}
          />
        </CollapseCard>
        <CollapseCard title="Module List">
          <ModuleList
            modules={modules.filter(
              (mod) =>
                !appliedFilters.project ||
                mod.projectId.toString() === appliedFilters.project,
            )}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default ModulePage;
